import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  UserRegisterRequest,
  UserRegisterResponse,
  OtpVerificationRequest,
  OtpVerificationResponse,
  ResendOtpRequest,
  ResendOtpResponse,
  ApiErrorResponse,
  UserLoginRequest,
  UserLoginResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  CheckEmailResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  TwoFactorStatusResponse,
  TwoFactorOtpResponse,
  TwoFactorVerifyResponse,
  SessionListResponse,
  DeleteSessionResponse,
  LogoutOthersResponse
} from '../models/api.model';

/**
 * Service for handling user-related API calls
 */
@Injectable({
  providedIn: 'root'
})
export class UserApiService {
  private readonly baseUrl = environment.API_BASE_URL;

  constructor(private http: HttpClient) { }

  /**
   * Register a new user
   * POST /register
   */
  register(userData: UserRegisterRequest): Observable<UserRegisterResponse> {
    const payload = { ...userData, email: userData.email.toLowerCase() };
    return this.http.post<UserRegisterResponse>(
      `${this.baseUrl}register`,
      payload
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Check if an email exists and return details for Google sign in validation
   * POST /check-email
   */
  checkEmail(email: string): Observable<CheckEmailResponse> {
    return this.http.post<CheckEmailResponse>(
      `${this.baseUrl}check-email`,
      { email: email.toLowerCase() }
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * User Login
   * POST /login
   */
  login(credentials: UserLoginRequest): Observable<UserLoginResponse> {
    const payload = { ...credentials, email: credentials.email.toLowerCase() };
    return this.http.post<UserLoginResponse>(
      `${this.baseUrl}login`,
      payload
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Get fresh CSRF token
   * GET /csrf-token
   */
  getCsrfToken(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}csrf-token`).pipe(
      catchError(error => this.handleError(error))
    );
  }


  /**
   * Verify OTP after registration
   * POST /verify-otp
   */
  verifyOtp(data: OtpVerificationRequest): Observable<OtpVerificationResponse> {
    const payload = { ...data, email: data.email.toLowerCase() };
    return this.http.post<OtpVerificationResponse>(
      `${this.baseUrl}verify-otp`,
      payload
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Resend OTP to user's email
   * POST /resend-otp
   */
  resendOtp(data: ResendOtpRequest): Observable<ResendOtpResponse> {
    const payload = { ...data, email: data.email.toLowerCase() };
    return this.http.post<ResendOtpResponse>(
      `${this.baseUrl}resend-otp`,
      payload
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Forgot Password - sends reset OTP
   * POST /forgot-password
   */
  forgotPassword(data: ForgotPasswordRequest): Observable<ForgotPasswordResponse> {
    const payload = { ...data, email: data.email.toLowerCase() };
    return this.http.post<ForgotPasswordResponse>(
      `${this.baseUrl}forgot-password`,
      payload
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Reset Password
   * POST /reset-password
   */
  resetPassword(data: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    const payload = { ...data, email: data.email.toLowerCase() };
    return this.http.post<ResetPasswordResponse>(
      `${this.baseUrl}reset-password`,
      payload
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Change Password
   * POST /change-password
   */
  changePassword(data: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(
      `${this.baseUrl}change-password`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Request Change Password OTP
   * POST /change-password/request-otp
   */
  requestChangePasswordOtp(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}change-password/request-otp`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Confirm Change Password
   * POST /change-password/confirm
   */
  confirmChangePassword(data: { otp: string }): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}change-password/confirm`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Get Two Factor Status
   * GET /2fa/status
   */
  get2FaStatus(): Observable<TwoFactorStatusResponse> {
    return this.http.get<TwoFactorStatusResponse>(
      `${this.baseUrl}2fa/status`
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Get Two Factor Status by email
   * GET /2fa-status?email=
   */
  get2FaStatusByEmail(email: string, token: string|undefined): Observable<TwoFactorStatusResponse> {
    return this.http.post<TwoFactorStatusResponse>(
      `${this.baseUrl}2fa/user-status`,
      { email: email.toLowerCase(),
        token: token
       }
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Get KYC Status
   * GET /users/kyc-status
   */
  getUserData(id: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}${id}`
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * List Sessions
   * GET /sessions
   */
  listSessions(): Observable<SessionListResponse> {
    return this.http.get<SessionListResponse>(
      `${this.baseUrl}sessions`
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Delete Session
   * DELETE /sessions/:sessionId
   */
  deleteSession(sessionId: string): Observable<DeleteSessionResponse> {
    return this.http.delete<DeleteSessionResponse>(
      `${this.baseUrl}sessions/${sessionId}`
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Logout
   * POST /logout
   */
  logout(): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}logout`,
      {}
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Logout Others
   * POST /sessions/logout-others
   */
  logoutOthers(): Observable<LogoutOthersResponse> {
    return this.http.post<LogoutOthersResponse>(
      `${this.baseUrl}sessions/logout-others`,
      {}
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Request Two Factor OTP
   * POST /2fa/request-otp
   */
  request2FaOtp(data: { action: string }): Observable<TwoFactorOtpResponse> {
    return this.http.post<TwoFactorOtpResponse>(
      `${this.baseUrl}2fa/request-otp`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Verify Two Factor OTP
   * POST /2fa/verify
   */
  verify2Fa(data: { action: string, otp: string }): Observable<TwoFactorVerifyResponse> {
    return this.http.post<TwoFactorVerifyResponse>(
      `${this.baseUrl}2fa/verify`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }


  /**
   * Update Account Preferences
   * PATCH /users/account-preferences
   */
  updateAccountPreferences(data: { currency_accounts: string[], self_custody_wallets?: string[] }): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}account-preferences`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Resubmit KYC document
   * POST /users/kyc/resubmit
   */
  resubmitKyc(imageId: string, data: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}kyc/resubmit/${imageId}`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Upload requested KYC document
   * POST /users/kyc/requested-docs/:imageId
   */
  uploadRequestedDoc(imageId: string, data: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}kyc/requested-docs/${imageId}`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.log('Raw API Error:', error);
    let errorMessage = 'An unexpected error occurred';
    let errors = undefined;

    if (error instanceof HttpErrorResponse) {
      // It is an HTTP Error
      if (error.error instanceof ErrorEvent) {
        // Client-side or network error
        errorMessage = error.error.message;
      } else {
        // Server-side error
        // Try to extract message from body
        if (error.error && typeof error.error === 'object') {
          if (error.error.message) {
            errorMessage = error.error.message;
          }
          if (error.error.errors) {
            errors = error.error.errors;
          }
        } else if (typeof error.error === 'string') {
          errorMessage = error.error; // Sometimes body is just text
        }

        // If we didn't find a specific message in body, fall back to status codes
        if (errorMessage === 'An unexpected error occurred' || errorMessage === undefined) {
          switch (error.status) {
            case 400: errorMessage = 'Invalid request.'; break;
            case 401: errorMessage = 'Unauthorized.'; break;
            case 403: errorMessage = 'Access denied.'; break;
            case 404: errorMessage = 'Resource not found.'; break;
            case 409: errorMessage = 'Conflict.'; break;
            case 422: errorMessage = 'Validation failed.'; break;
            case 500: errorMessage = 'Server error.'; break;
            default: errorMessage = `Error: ${error.status} - ${error.statusText}`;
          }
        }
      }
    } else {
      // Not an HttpErrorResponse (e.g. client code error)
      errorMessage = error.message || error.toString();
    }

    console.error('Processed API Error:', errorMessage);

    return throwError(() => ({
      status: false,
      message: errorMessage,
      errors: errors,
      data: error.error?.data // Pass data if available in error response
    } as ApiErrorResponse));
  }

  getKycList(): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}kyc/steps`
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Get RFI Notes
   * GET /users/rfi-notes
   */
  getRfiNotes(): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}rfi-notes`
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Get profile settings
   * GET /users/profile-settings
   */
  getProfileSettings(): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}profile-settings`
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Update profile settings
   * PATCH /users/profile-settings
   */
  updateProfileSettings(data: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}profile-settings`,data)
  }

  /**
   * Deactivate Account
   * POST /user/deactivate-account
   */
  deactivateAccount(): Observable<any> {
    const url = `${this.baseUrl}deactivate-account`;

    return this.http.post<any>(url, {}).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Cancel Deactivation
   * POST /user/cancel-deactivation
   */
  cancelDeactivation(): Observable<any> {
    const url = `${this.baseUrl}cancel-deactivation`;

    return this.http.post<any>(url, {}).pipe(
      catchError(error => this.handleError(error))
    );
  }



  /**
   * Create user card request
   * POST /users/card
   * Payload example: { card_type: "Gaulbridge Platinum" }
   */
  createCard(data: { card_type: string }): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}card`,
      data
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Delete user profile image
   * POST /users/profile-image
   */
  deleteProfileImage(userId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}profile-image`,
      { body: { user_id: userId } }
    ).pipe(
      catchError(error => this.handleError(error))
    );
  }

}
